import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { mono, accent, Empty } from '../../../styles/techComponents';

const tickStyle = { fontSize: 11, fontFamily: mono, fill: 'var(--text-disabled)' };
const tooltipStyle = {
  background: 'rgba(11, 19, 32, 0.95)',
  border: '1px solid rgba(0, 180, 216, 0.4)',
  borderRadius: 4,
  fontFamily: mono,
  fontSize: 12,
  color: 'var(--text-primary)',
};
const legendStyle = {
  paddingTop: 10,
  fontFamily: mono,
  fontSize: 11,
  letterSpacing: '0.04em',
};

const COLORS = ['#00B4D8', '#06D6A0', '#FFD166', '#7C8AA0', '#A4B0C2', '#4D5C72', '#C0B6F0', '#86E1E5'];

/* ---------- Trend: noticias por día (últimos 30) ---------- */

const TrendChart = ({ news = [] }) => {
  const data = useMemo(() => {
    const map = new Map();
    for (const n of news) {
      const ts = n.published_at || n.fetched_at;
      if (!ts) continue;
      const d = new Date(ts);
      if (isNaN(d)) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()]
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .slice(-30)
      .map(([key, value]) => {
        const d = new Date(key);
        return { name: d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' }), value };
      });
  }, [news]);

  if (data.length === 0) return <Empty>Sin datos para graficar todavía.</Empty>;

  return (
    <div style={{ height: 260 }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="g-news" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity={0.5} />
              <stop offset="100%" stopColor={accent} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
          <XAxis dataKey="name" stroke="var(--text-disabled)" tick={tickStyle} />
          <YAxis stroke="var(--text-disabled)" tick={tickStyle} allowDecimals={false} />
          <Tooltip contentStyle={tooltipStyle} />
          <Area type="monotone" dataKey="value" name="Notas indexadas" stroke={accent} strokeWidth={2.4} fill="url(#g-news)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ---------- Distribución por fuente ---------- */

const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" style={{ fontFamily: mono, fontSize: 11 }}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const DistributionChart = ({ news = [] }) => {
  const data = useMemo(() => {
    const map = {};
    for (const n of news) {
      const k = n.source || 'sin fuente';
      map[k] = (map[k] || 0) + 1;
    }
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }));
  }, [news]);

  if (data.length === 0) return <Empty>Sin fuentes registradas.</Empty>;

  return (
    <div style={{ height: 250 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            outerRadius={86}
            innerRadius={45}
            dataKey="value"
            label={renderLabel}
            stroke="rgba(11, 19, 32, 0.7)"
            strokeWidth={2}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={COLORS[i % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={legendStyle} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export { TrendChart, DistributionChart };
